import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence, useScroll, useMotionValueEvent } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Menu, X, ChevronUp } from "lucide-react";
import logo from "@/assets/logo-coressen-white.svg";

const navItems = [
  { label: "Mandamientos", href: "#mandamientos", id: "mandamientos" },
  { label: "Nuestro Método", href: "#desafio", id: "desafio" },
  { label: "Quiénes Somos", href: "#quienes-somos", id: "quienes-somos" },
  { label: "Testimonios", href: "#testimonios", id: "testimonios" },
];

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeSection, setActiveSection] = useState("");
  const [isScrolled, setIsScrolled] = useState(false);
  const [showBackToTop, setShowBackToTop] = useState(false);
  
  const { scrollYProgress } = useScroll();

  // Track scroll position for navbar styling and back-to-top button
  useMotionValueEvent(scrollYProgress, "change", (latest) => {
    setIsScrolled(latest > 0.02);
    setShowBackToTop(latest > 0.15);
  });

  // Intersection Observer for active section detection
  useEffect(() => {
    const observers: IntersectionObserver[] = [];
    
    navItems.forEach((item) => {
      const element = document.getElementById(item.id);
      if (element) {
        const observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                setActiveSection(item.id);
              }
            });
          },
          { threshold: 0.3, rootMargin: "-80px 0px -50% 0px" }
        );
        observer.observe(element);
        observers.push(observer);
      }
    });

    return () => observers.forEach((obs) => obs.disconnect());
  }, []);

  // Load Calendly script
  useEffect(() => {
    const script = document.createElement("script");
    script.src = "https://assets.calendly.com/assets/external/widget.js";
    script.async = true;
    document.body.appendChild(script);

    return () => {
      if (document.body.contains(script)) {
        document.body.removeChild(script);
      }
    };
  }, []);

  const scrollToSection = useCallback((href: string) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
    setIsOpen(false);
  }, []);

  const scrollToTop = useCallback(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  const scrollToCalendly = useCallback(() => {
    const el = document.getElementById("calendly");
    if (el) {
      el.scrollIntoView({ behavior: "smooth" });
    }
    setIsOpen(false);
  }, []);

  const openCalendly = useCallback(() => {
    if (window.Calendly) {
      window.Calendly.initPopupWidget({
        url: "https://calendly.com/asesor-coressenglobal/llamada",
      });
    }
    setIsOpen(false);
  }, []);

  return (
    <>
      {/* Scroll Progress Indicator */}
      <motion.div
        className="fixed top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary via-primary/80 to-primary z-[60] origin-left"
        style={{ scaleX: scrollYProgress }}
      />

      <motion.nav
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="fixed top-1 left-0 right-0 z-50"
      >
        {/* Dynamic Background */}
        <motion.div
          className="absolute inset-0 border-b transition-all duration-300"
          animate={{
            backgroundColor: isScrolled ? "rgba(0, 0, 0, 0.85)" : "rgba(0, 0, 0, 0.4)",
            backdropFilter: isScrolled ? "blur(20px)" : "blur(12px)",
            borderColor: isScrolled ? "rgba(255, 255, 255, 0.15)" : "rgba(255, 255, 255, 0.1)",
          }}
        />

        <div className="container relative z-10 px-4">
          <motion.div
            className="flex items-center justify-between"
            animate={{ height: isScrolled ? "56px" : "72px" }}
            transition={{ duration: 0.3 }}
          >
            {/* Logo */}
            <motion.a
              href="#hero"
              onClick={() => scrollToSection("#hero")}
              className="flex items-center"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <motion.img
                src={logo}
                alt="CORESSEN"
                className="w-auto transition-all duration-300"
                animate={{ height: isScrolled ? "36px" : "48px" }}
              />
            </motion.a>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-0.5">
              {navItems.map((item) => (
                <motion.button
                  key={item.label}
                  onClick={() => scrollToSection(item.href)}
                  className="relative px-4 py-2 text-sm font-medium transition-colors"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <span
                    className={`relative z-10 transition-colors duration-200 ${
                      activeSection === item.id
                        ? "text-white"
                        : "text-white/60 hover:text-white/90"
                    }`}
                  >
                    {item.label}
                  </span>
                  
                  {/* Active indicator */}
                  {activeSection === item.id && (
                    <motion.div
                      layoutId="activeSection"
                      className="absolute inset-0 bg-white/10 rounded-full"
                      transition={{ type: "spring", stiffness: 380, damping: 30 }}
                    />
                  )}
                  
                  {/* Hover underline */}
                  <motion.span
                    className="absolute bottom-1 left-1/2 h-0.5 bg-gradient-to-r from-primary to-primary/60 rounded-full"
                    initial={{ width: 0, x: "-50%" }}
                    whileHover={{ width: "60%" }}
                    transition={{ duration: 0.2 }}
                  />
                </motion.button>
              ))}
              
              <motion.button
                onClick={openCalendly}
                className="ml-2 px-4 py-2 text-sm font-semibold text-[#FF9FBD] hover:text-white transition-colors relative group"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <span className="relative z-10">Aplicar Llamada</span>
                <motion.span
                  className="absolute inset-0 bg-[#FF9FBD]/15 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                />
              </motion.button>
            </div>

            {/* CTA Button */}
            <div className="hidden lg:block">
              <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }}>
                <Button
                  variant="gold"
                  size="sm"
                  onClick={scrollToCalendly}
                  className="shadow-lg shadow-primary/20"
                >
                  Agendar Llamada
                </Button>
              </motion.div>
            </div>

            {/* Mobile Menu Button */}
            <motion.button
              onClick={() => setIsOpen(!isOpen)}
              className="lg:hidden p-2 text-white/80 hover:text-white transition-colors rounded-lg hover:bg-white/5"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <AnimatePresence mode="wait">
                {isOpen ? (
                  <motion.div
                    key="close"
                    initial={{ rotate: -90, opacity: 0 }}
                    animate={{ rotate: 0, opacity: 1 }}
                    exit={{ rotate: 90, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <X className="w-6 h-6" />
                  </motion.div>
                ) : (
                  <motion.div
                    key="menu"
                    initial={{ rotate: 90, opacity: 0 }}
                    animate={{ rotate: 0, opacity: 1 }}
                    exit={{ rotate: -90, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                  >
                    <Menu className="w-6 h-6" />
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.button>
          </motion.div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
              className="lg:hidden absolute top-full left-0 right-0 bg-black/95 backdrop-blur-xl border-b border-white/10 overflow-hidden"
            >
              <div className="container px-4 py-6 flex flex-col gap-1">
                {navItems.map((item, index) => (
                  <motion.button
                    key={item.label}
                    initial={{ opacity: 0, x: -30 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -30 }}
                    transition={{ delay: index * 0.05, duration: 0.3 }}
                    onClick={() => scrollToSection(item.href)}
                    className={`px-4 py-3.5 text-left rounded-xl transition-all duration-200 ${
                      activeSection === item.id
                        ? "text-white bg-white/10"
                        : "text-white/70 hover:text-white hover:bg-white/5"
                    }`}
                  >
                    <span className="flex items-center gap-3">
                      {activeSection === item.id && (
                        <motion.span
                          layoutId="mobileActive"
                          className="w-1.5 h-1.5 rounded-full bg-primary"
                        />
                      )}
                      {item.label}
                    </span>
                  </motion.button>
                ))}
                
                <motion.div
                  initial={{ opacity: 0, x: -30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: navItems.length * 0.05, duration: 0.3 }}
                  className="mt-2 pt-4 border-t border-white/10"
                >
                  <button
                    onClick={openCalendly}
                    className="w-full px-4 py-3.5 text-left text-[#FF9FBD] font-semibold hover:bg-white/5 rounded-xl transition-colors"
                  >
                    Aplicar Llamada
                  </button>
                </motion.div>
                
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: (navItems.length + 1) * 0.05, duration: 0.3 }}
                  className="mt-4"
                >
                  <Button
                    variant="gold"
                    className="w-full shadow-lg shadow-primary/20"
                    onClick={scrollToCalendly}
                  >
                    Agendar Llamada
                  </Button>
                </motion.div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.nav>

      {/* Back to Top Button */}
      <AnimatePresence>
        {showBackToTop && (
          <motion.button
            initial={{ opacity: 0, scale: 0.8, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.8, y: 20 }}
            transition={{ type: "spring", stiffness: 300, damping: 25 }}
            onClick={scrollToTop}
            className="fixed bottom-24 right-6 z-40 p-3 bg-white/10 backdrop-blur-lg border border-white/20 rounded-full text-white hover:bg-white/20 transition-colors shadow-lg"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
          >
            <ChevronUp className="w-5 h-5" />
          </motion.button>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
